'use client'
import { useState, useEffect } from 'react'
import type { FieldErrors, UseFormRegister } from 'react-hook-form'
import Chevron from '../svg/icon/Chevron'
import clsx from 'clsx'
import { sanitizeFieldName } from '@/utils/sanitizedLabel'

type Props = {
	id: string
	options: string[]
	label: string
	variant?: 'primary' | 'secondary' | 'tertiary'
	alignment?: 'center' | 'space-between'
	colSpan?: string
	formStyle?: string
	placeholder?: string
	required?: boolean
	register?: UseFormRegister<any>
	errors?: FieldErrors
	className?: string
	value?: string
	onChange?: (value: string) => void
	inputRef?: React.Ref<HTMLSelectElement>
} & Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'value' | 'onChange'>

export default function Select({
	id,
	options,
	label,
	variant = 'primary',
	colSpan = '1',
	placeholder,
	required = false,
	alignment = 'center',
	formStyle = 'box',
	register,
	errors,
	className,
	value,
	onChange,
	inputRef,
	...props
}: Props) {
	const [selectedValue, setSelectedValue] = useState(value || '')
	const fieldName = sanitizeFieldName(label)

	// Update internal state when controlled value changes
	useEffect(() => {
		if (value !== undefined) {
			setSelectedValue(value)
		}
	}, [value])

	// For uncontrolled components
	useEffect(() => {
		if (!onChange && !value) {
			const select = document.getElementById(id) as HTMLSelectElement
			if (select && select.value === '') {
				setSelectedValue('')
			}
		}
	}, [id, onChange, value])

	const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const newValue = e.target.value
		setSelectedValue(newValue)

		if (onChange) {
			onChange(newValue)
		}
	}

	return (
		<div className={clsx('form-component', colSpan === '2' && 'colSpan2')}>
			<div
				className={clsx(
					'form-element-container select relative',
					selectedValue !== '' && 'selected',
					formStyle === 'line' && 'line-style',
					{
						primary: `${alignment === 'center' && 'text-contrast hover:bg-white-frosted-shade'} [&_svg]:text-current bg-white-frosted`,
						secondary: 'text-white bg-blue-dark hover:bg-contrast',
						tertiary: 'text-white bg-blue hover:bg-blue-shade',
					}[variant],
					alignment === 'center' ? 'uppercase font-medium' : 'justify-between',
					className
				)}
			>
				{register ? (
					// Uncontrolled component with react-hook-form register
					<select
						id={id}
						{...register(fieldName, {
							required: required && `${label} is required`,
							onChange: (e) => setSelectedValue(e.target.value),
						})}
						className="w-full h-full absolute inset-0 opacity-0 cursor-pointer"
						{...props}
					>
						<option value="">{placeholder || `Select ${label}`}</option>
						{options.map((option, index) => (
							<option key={index} value={option}>
								{option}
							</option>
						))}
					</select>
				) : (
					// Controlled component for use with Controller
					<select
						id={id}
						ref={inputRef}
						value={value || ''}
						onChange={handleChange}
						className="w-full h-full absolute inset-0 opacity-0 cursor-pointer"
						{...props}
					>
						<option value="">{placeholder || `Select ${label}`}</option>
						{options.map((option, index) => (
							<option key={index} value={option}>
								{option}
							</option>
						))}
					</select>
				)}

				{/* Selected Value Display */}
				<span className="selected-value min-h-6 pointer-events-none">{selectedValue}</span>

				<Chevron className="pointer-events-none" />
			</div>

			<label htmlFor={id} className="opacity-60 pointer-events-none">
				{`${label} ${required ? ' *' : ''}`}
			</label>

			{errors && errors[fieldName] && (
				<span className="mt-1 text-red-500">{errors[fieldName]?.message?.toString()}</span>
			)}
		</div>
	)
}
